import React, { Fragment, useState } from 'react'
import {
  List,
  Datagrid,
  TextField,
  Filter,
  ReferenceInput,
  SelectInput,
  TextInput,
  ReferenceArrayInput,
  SelectArrayInput,
  ShowButton,
  EditButton,
  downloadCSV,
  FunctionField,
  Pagination,
  useDataProvider
} from 'react-admin'
import { capitalCase } from 'change-case'
import jsonExport from 'jsonexport/dist'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import HealthIndicator, { HealthIndicatorProvider } from '../components/HealthIndicator'
import TimeIndicator from '../components/TimeIndicator'
import ScannerIcon from '@material-ui/icons/SettingsRemote'
import FolderIcon from '@material-ui/icons/Folder'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import TagIcon from './components/TagIcon'
import FileBrowserModal from '../components/FileBrowserModal'
import { VncEnabledContext, VncEnabledProvider } from './VncEnabledProvider'
import * as storage from '../lib/storage'

const useStyles = makeStyles(theme => ({
  textInput: { width: 400, marginRight: 10 },
  selectInput: { width: 200 },
  field: {
    display: 'flex'
  },
  actions: {
    display: 'flex'
  },
  indicator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
}))

const exporter = modalities => {
  const modalitiesForExport = modalities.map(modality => {
    const {
      dashboardIframeUrl,
      logstreamIframeUrl,
      relationshipId,
      metrics,
      relationship,
      ...modalityForExport
    } = modality
    return modalityForExport
  })
  jsonExport(
    modalitiesForExport,
    {
      headers: [
        'id',
        'serialNumber',
        'siteIdNumber',
        'manufacturer',
        'modality',
        'model',
        'siteName',
        'heLevel',
        'heLevelTime',
        'siteAddress',
        'latitude',
        'longitude',
        'softwareVersion',
        'createdDate'
      ]
    },
    (err, csv) => {
      downloadCSV(csv, 'modalities')
    }
  )
}

const ModalityFilter = props => {
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const [tagTypes, setTagTypes] = React.useState([])
  const accountId = storage.get('user', {})?.accountId

  React.useEffect(() => {
    const fetchTagTypes = async () => {
      try {
        const { data } = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: accountId ? { accountId } : {},
        })
        setTagTypes(data || [])
      } catch (err) {
        console.error('Error fetching tag types:', err)
      }
    }

    fetchTagTypes()
  }, [accountId, dataProvider])

  // Key prop forces Filter to re-render when tagTypes change, ensuring React-Admin detects new filters
  return (
    <Filter {...props} key={`filter-${tagTypes.length}`}>
      <TextInput
        label='Search'
        source='q'
        alwaysOn
        className={classes.textInput}
      />
      <ReferenceInput
        label='Device Type'
        source='type'
        reference='modalities/types'
        allowEmpty
      >
        <SelectInput optionText='name' />
      </ReferenceInput>
      <SelectInput
        label='Filter Active/Inactive'
        source='active'
        alwaysOn
        className={classes.selectInput}
        choices={[
          { id: 1, name: 'Only Active' },
          { id: 0, name: 'Only Inactive' },
          { id: 2, name: 'Show All' }
        ]}
      />
      <SelectInput
        label='Connectivity'
        source='connectivity'
        className={classes.selectInput}
        choices={[
          { id: 'all', name: 'All' },
          { id: 'online', name: 'Online only' },
          { id: 'offline', name: 'Offline only' }
        ]}
      />
      {tagTypes.map((tagType) => (
        <ReferenceArrayInput
          key={tagType.id}
          source={`tag_${tagType.name}`}
          reference="tags"
          label={`Tag: ${tagType.name}`}
          filter={accountId ? { tagTypeId: tagType.id, accountId } : { tagTypeId: tagType.id }}
          alwaysOn={false}
          style={{ minWidth: '150px' }}
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>
      ))}
    </Filter>
  )
}

const ModalityPanel = ({ id, record, resource }) => {
  const FIELDS = [
    'assetNumber',
    'bedName',
    'unitName',
    'inactiveSoftwareVersion',
    'activeSoftwareVersion',
    'systemModel',
    'macAddress',
    'networkMacAddress',
    'ixNetworkIpAddress',
    'mcNetworkIpAddress',
    'pdmType',
    'pdmMacAddress',
    'pdmSerialNumber',
    'pdmSoftwareVersion'
  ]

  const renderField = key =>
    record[key] && (
      <Box key={'field-' + key} mx={2} my={1} width={180}>
        <Typography variant='body2' color='textSecondary'>
          {capitalCase(key)}
        </Typography>{' '}
        <Typography variant='body2'>{record[key]}</Typography>
      </Box>
    )

  return (
    <Box
      my={0}
      mx={2}
      display='flex'
      alignItems='center'
      justifyContent='flex-start'
      flexWrap='wrap'
      flexDirection='row'
    >
      {record.siteAddress && (
        <Box mx={2} my={2} width={180}>
          <Typography variant='body2' color='textSecondary'>
            Location
          </Typography>{' '}
          <Typography variant='body2'>{record.siteAddress}</Typography>
        </Box>
      )}

      {record.credentials && record.manufacturer === 'Siemens' && (
        <Fragment>
          {record.credentials.username && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Login
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.username}
              </Typography>
            </Box>
          )}
          {record.credentials.password && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Key
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.password}
              </Typography>
            </Box>
          )}
          {record.credentials.expiration && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Expiration
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.expiration}
              </Typography>
            </Box>
          )}
          {record.credentials.flexId && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Flex ID
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.flexId}
              </Typography>
            </Box>
          )}
        </Fragment>
      )}

      {FIELDS.map(renderField)}
    </Box>
  )
}

const RemoteScanButton = ({ record }) => {
  const { vncEnabledIds } = React.useContext(VncEnabledContext)
  const [loading, setLoading] = React.useState(false)

  // Only show button if this modality has VNC configured
  if (!record?.id || !vncEnabledIds.includes(record.id)) return null

  const handleClick = async (e) => {
    e.stopPropagation()
    
    if (loading) return
    setLoading(true)

    try {
      // Get auth headers from session (uses AWS Amplify)
      const { getAuthHeaders } = await import('../lib/session')
      const authHeaders = await getAuthHeaders()
      
      // Use direct fetch instead of dataProvider to avoid response transformation
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001'
      const response = await fetch(`${apiUrl}/vnc/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ modalityId: record.id })
      })

      const responseText = await response.text()
      console.log('VNC session raw response:', responseText)
      
      if (!response.ok) {
        let errorMessage = `HTTP error ${response.status}`
        try {
          const errorData = JSON.parse(responseText)
          errorMessage = errorData.message || errorMessage
        } catch (e) {}
        throw new Error(errorMessage)
      }

      const sessionData = JSON.parse(responseText)
      console.log('VNC session parsed response:', sessionData)

      // Handle potential nested data structure
      const data = sessionData.data || sessionData
      const sessionToken = data.sessionToken
      const wssUrl = data.wssUrl
      
      console.log('Extracted - sessionToken:', sessionToken, 'wssUrl:', wssUrl)

      if (!sessionToken) {
        throw new Error('No session token returned from API')
      }

      // Open VNC viewer in a new window with the session token
      const width = 1280
      const height = 800
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2
      
      // Pass session info via URL params - include wssUrl for the viewer to use
      const viewerUrl = `/vnc-viewer.html?token=${sessionToken}&modalityId=${record.id}&wssUrl=${encodeURIComponent(wssUrl)}`
      
      window.open(
        viewerUrl,
        `vnc-${record.id}`,
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`
      )
    } catch (error) {
      console.error('Failed to create VNC session:', error)
      alert(error.message || 'Failed to start VNC session. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip title="Remote Access">
      <IconButton 
        color='primary' 
        onClick={handleClick}
        disabled={loading}
      >
        <ScannerIcon />
      </IconButton>
    </Tooltip>
  )
}

const FileBrowserButton = ({ record }) => {
  const { fileTransferEnabledIds } = React.useContext(VncEnabledContext)
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false)

  // Only show button if this modality has file transfer enabled
  if (!record?.id || !fileTransferEnabledIds.includes(record.id)) return null

  const handleClick = (e) => {
    e.stopPropagation()
    setFileBrowserOpen(true)
  }

  const handleClose = () => {
    setFileBrowserOpen(false)
  }

  return (
    <>
      <Tooltip title="Browse Files">
        <IconButton 
          color='primary' 
          onClick={handleClick}
        >
          <FolderIcon />
        </IconButton>
      </Tooltip>
      <FileBrowserModal
        open={fileBrowserOpen}
        onClose={handleClose}
        modalityId={record.id}
        modalityName={record.siteName || record.displayName}
        initialPath="C:"
      />
    </>
  )
}

const Actions = (record) => {
  const classes = useStyles()
  return (
    <div className={classes.actions}>
      <ShowButton record={record} label='' basePath='/modalities' />
      <EditButton record={record} label='' basePath='/modalities' />
      {record && <RemoteScanButton record={record} />}
      {record && <FileBrowserButton record={record} />}
    </div>
  )
}

const ModalityList = props => {
  return (
    <VncEnabledProvider>
      <HealthIndicatorProvider>
        <List
          filters={<ModalityFilter />}
          filterDefaultValues={{ active: 1 }}
          exporter={exporter}
          bulkActionButtons={false}
          title='Devices'
          sort={{ field: 'healthStatusLevel', order: 'DESC' }}
          perPage={50}
          pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
          {...props}
        >
          <Datagrid size='small' rowClick='show' expand={<ModalityPanel />}>
            <FunctionField
              label='Status'
              sortBy='healthStatusLevel'
              render={props => <HealthIndicator {...props} />}
            />
          <FunctionField
            label='Time'
            sortBy='timeStatusLevel'
            render={props => <TimeIndicator {...props} />}
          />
          <TextField source='id' />
          <TextField source='siteName' />
          <TextField source='serialNumber' />
          <TextField source='manufacturer' />
          <TextField source='modality' label='Device' />
          <TextField source='model' />
          <FunctionField
            label='Metrics'
            render={props => (
              <div>
                {props.timeStatusLevel !== 2 ? (
                  props.metrics
                ) : (
                  <span style={{ opacity: 0.5, fontStyle: 'italic' }}>
                    {`Telemetry data is >48 hrs old on this device`}
                  </span>
                )}
              </div>
            )}
          />
          <FunctionField
            label='Tags'
            render={record => <TagIcon record={record} />}
          />
          <FunctionField
            label='Actions'
            render={props => <Actions {...props} />}
          />
          </Datagrid>
        </List>
      </HealthIndicatorProvider>
    </VncEnabledProvider>
  )
}

export default ModalityList
